// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { createPipelineRequest } from '@azure/core-rest-pipeline';
import {
    ConfigSettingsAndFeatureFlags,
    createSettingsAndFeatureFlags,
    parseSettingAndFeatureFlags,
} from './configSettingsAndFeatureFlags';
import { CreateOrUpdateUserCurriculumResponse, Curriculum, VsCodeEduClient } from './generated';
import { HttpStatusCode, assert, isNonEmptyString } from './utilities';
import { OmitReadonly } from './utilities/types';

// Target service environment.
export enum ApiEnvironment {
    LocalHost = 'dev',
    PreProduction = 'ppe',
    Beta = 'beta',
    Production = 'prod',
}

const apiEndpoint: { [key in ApiEnvironment]: string } = {
    dev: 'http://localhost:3002',
    ppe: 'https://ppe.vscodeedu.com/api',
    beta: 'https://vscodeedu.com/api',
    prod: 'https://vscodeedu.com/api',
};

/**
 * Extended client for the VS Code for Education APIs.
 * Includes operations to access non-API content.
 */
export class VsCodeEduClientExt extends VsCodeEduClient {
    /**
     * Initializes a new instance of the VsCodeEduClientExt class.
     * @param environment Environment to construct client for.
     */
    constructor(public readonly environment: ApiEnvironment) {
        super({
            // eslint-disable-next-line security/detect-object-injection
            $host: apiEndpoint[environment],
            // eslint-disable-next-line security/detect-object-injection
            endpoint: apiEndpoint[environment],
            allowInsecureConnection: environment === ApiEnvironment.LocalHost,
        });
    }

    // Returns content URL prefix for an environment.
    public get contentUrlPrefix(): string {
        switch (this.environment) {
            case ApiEnvironment.LocalHost:
                return 'http://localhost:3002';
            case ApiEnvironment.PreProduction:
                return 'https://ppe.vscodeedu.com';
            case ApiEnvironment.Production:
            case ApiEnvironment.Beta:
                return 'https://vscodeedu.com';
            default:
                assert.unreachable(this.environment, 'environment');
        }
    }

    /**
     * Calls a GET and returns the response body as text.
     * @param url URL to fetch.
     * @returns Response body as text. Empty string if the response is NoContent.
     */
    async fetch(url: string): Promise<string> {
        const request = createPipelineRequest({
            method: 'GET',
            url,
            allowInsecureConnection: this.environment === ApiEnvironment.LocalHost,
        });
        const response = await this.sendRequest(request);
        if (response.status === HttpStatusCode.NoContent) {
            return '';
        } else if (response.status !== HttpStatusCode.Ok || !isNonEmptyString(response.bodyAsText)) {
            throw Error(`Failed to GET '${url}'. Response status: ${response.status}.`);
        } else {
            return response.bodyAsText;
        }
    }

    /**
     * Retrieves contents of the VS Code profile with specified name.
     * Throws an exception if the request was not successful.
     * @param profileName The name of the profile to retrieve.
     */
    async getProfile(profileName: string): Promise<string> {
        return this.fetch(`${this.contentUrlPrefix}/profiles/${profileName}.code-profile`);
    }

    /**
     * Retrieves contents of the specified content file.
     * Throws an exception if the request was not successful.
     * @param relativeUri The contentUri from a Lesson in the form /content/{relative-path}.
     * @returns The content of the file as a string. Empty string if there is no content.
     */
    async getContent(relativeUri: string): Promise<string> {
        const suffix = relativeUri.startsWith('$') ? relativeUri.substring(1) : relativeUri;
        const url = `${this.contentUrlPrefix}${suffix}`;
        return this.fetch(url);
    }

    /**
     * Gets the feature flags and settings from the config API for the current environment.
     * @returns The feature flags from the config API.
     */
    async getConfigSettingsAndFeatureFlags(): Promise<ConfigSettingsAndFeatureFlags> {
        const config = await this.getConfig();
        return parseSettingAndFeatureFlags(this.environment, config);
    }

    /**
     * Patches the feature flags and settings using the config API for the current environment.
     * Note: This method requires the user to have the appropriate permissions.
     * @param config The feature flags and settings to patch.
     */
    async patchConfigSettingsAndFeatureFlags(config: ConfigSettingsAndFeatureFlags): Promise<void> {
        const processedConfig = createSettingsAndFeatureFlags(this.environment, config);
        await this.patchConfig({ body: processedConfig });
    }

    /**
     * Updates user curriculum information.
     * @param userId Unique identifier of the user.
     * @param curriculumId Unique identifier of a curriculum.
     * @param body Curriculum parameters.
     */
    async createUserCurriculum(
        userId: string,
        curriculumId: string,
        body: OmitReadonly<Curriculum>
    ): Promise<CreateOrUpdateUserCurriculumResponse> {
        return this.createOrUpdateUserCurriculum(userId, curriculumId, { body: body as unknown as Curriculum });
    }
}
